import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';

import { fg } from '@confluence/feature-gating';

type TransitionPreloaderProps = {
	children: ReactNode;
	preloadUrl: string;
	onlyJS?: boolean;
};

const PRELOAD_TIMER = 100;

const TransitionPreloaderImpl = ({ children, preloadUrl, onlyJS }: TransitionPreloaderProps) => {
	const hoverTimeoutRef = useRef<number | null>(null);

	const onHover = useCallback(() => {
		window.dispatchEvent(
			new CustomEvent('preloadOnHover', {
				bubbles: true,
				detail: { preloadUrl, onlyJS },
			}),
		);
	}, [preloadUrl, onlyJS]);

	useEffect(() => {
		return () => {
			if (hoverTimeoutRef.current) {
				window.clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, []);

	// Median user reaction time (from hover to click) is ~250 ms for small elements such as page tree item.
	// Hence for JS only we don't want to wait for the hover timeout and preload components immediately.
	const preloadTimeout = onlyJS ? 0 : PRELOAD_TIMER;

	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, @atlassian/a11y/interactive-element-not-keyboard-focusable
		<div
			data-testid="hover-preloader"
			onMouseEnter={() => {
				if (hoverTimeoutRef.current) {
					window.clearTimeout(hoverTimeoutRef.current);
				}
				hoverTimeoutRef.current = window.setTimeout(() => {
					onHover();
				}, preloadTimeout);
			}}
			onMouseLeave={() => {
				if (hoverTimeoutRef.current) {
					window.clearTimeout(hoverTimeoutRef.current);
				}
			}}
		>
			{children}
		</div>
	);
};

export const HoverPreloader = (props: TransitionPreloaderProps) => {
	if (!props.preloadUrl) {
		return <>{props.children}</>;
	}
	return <TransitionPreloaderImpl {...props} />;
};

export const HoverPreloaderWithGate = (props: TransitionPreloaderProps) => {
	if (!props.preloadUrl || !fg('confluence_preload_js_hover')) {
		return <>{props.children}</>;
	}

	return <TransitionPreloaderImpl {...props} />;
};
